import { DataFilterField, DataFilterValues, isRule } from './data-filter.component';

/**
 * A filter bar's values as the repeated `rule` query parameter the server
 * reads - one string per rule, each `field:op:value`.
 *
 * The Leads board and the customers table each had their own copy of this
 * loop, and the roll-off board was about to be the third. The loop is small;
 * what is worth having once is the two decisions inside it. A choice column
 * carries a bare value and means `eq`, because "is" is the only thing a pick
 * from a list can mean. And an empty or cleared value sends nothing, because a
 * filter somebody took off must widen the query rather than ask for the rows
 * whose title is the empty string.
 *
 * The server holds the whitelist of columns and comparisons and answers 400
 * on anything else, so a rule a page invents is a visible failure rather than
 * a filter that quietly does nothing.
 *
 * @param fields  When given, only these columns are read - so a page whose
 *                value object also holds filters with query parameters of
 *                their own (a pinned customer type, say) cannot send one of
 *                those a second time as a rule the server would refuse.
 * @param drop    Columns to leave out this time. A page uses this when another
 *                control already narrows the same column - the Leads board's
 *                "Assigned to me" chip and its Owner rule - and sending both
 *                would AND them into an empty result instead of answering
 *                either question.
 */
export function toRuleParams(
  values: DataFilterValues,
  fields?: readonly DataFilterField[],
  drop: readonly string[] = [],
): string[] {
  const ids = fields ? fields.map(field => field.id) : Object.keys(values ?? {});
  const rules: string[] = [];
  for (const field of ids) {
    if (drop.includes(field)) continue;
    const held = values[field];
    if (held === null || held === undefined || held === '') continue;
    const op = isRule(held) ? held.op : 'eq';
    const value = isRule(held) ? held.value : held;
    rules.push(`${field}:${op}:${value}`);
  }
  return rules;
}
